// ============================================================================
//  VIEW: HostDashboardView.jsx - the host's home screen
// ============================================================================
//  The screen behind the Home tab in host mode. Big scrollable page with:
//
//    - Earnings card at the top, showing available balance (what they can
//      cash out) and pending earnings (money locked in active/future bookings).
//    - Active Guests section - drivers currently parked, with a live
//      green indicator and a message/report action pair.
//    - Upcoming Guests - drivers who've booked a future slot.
//    - Past Bookings - completed stays (last 5 shown).
//    - Reports - flags that support has raised about this host's listings
//      (only shown if there are any).
//    - Your Driveways - the host's listings with a toggle, edit pencil,
//      and an inline preview of their latest reviews.
// ============================================================================

import React from 'react';
import { Pencil, MessageCircle, Star, Flag, Users, CalendarX, CalendarClock, PlusCircle, AlertTriangle } from 'lucide-react';
import HostNav from '../shared/HostNav';

// Format a Firestore Timestamp or ISO date as "20 Apr 2026".
const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Format an ISO string as "Sat, 20 Apr · 14:30" for upcoming booking rows.
const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    + ' · '
    + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const HostDashboardView = ({
  myHostEarnings,
  availableBalance,
  pendingEarnings = 0,
  hostListings,
  allBookings,
  activeHostBookings,
  upcomingHostBookings = [],
  pastHostBookings,
  hostReports = [],
  currentScreen,
  onNavigate,
  onToggleListing,
  onEditSpot,
  onMessageDriver,
  onReport,
  onRequestPayout,
}) => (
  <div className="screen" style={{padding: 0}}>
    <div style={{flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px 20px 100px 20px'}}>
    <div className="host-header">
      <h2 style={{margin: 0, fontSize: 24, fontWeight: 800}}>Host Dashboard</h2>
    </div>

    {/* ── Earnings summary card ── */}
    <div className="earnings-card">
      <p className="earnings-title">Available Balance</p>
      {/* Falls back to total earnings if availableBalance isn't passed in */}
      <p className="earnings-amount">£{(availableBalance ?? myHostEarnings).toFixed(2)}</p>
      {/* Show the total lifetime earnings if there's been a payout */}
      {availableBalance !== undefined && myHostEarnings > availableBalance && (
        <p style={{margin: '4px 0 0', fontSize: 13, opacity: 0.8}}>
          Total earned: £{myHostEarnings.toFixed(2)}
        </p>
      )}
      {/* Pending = money that'll become available once the session completes */}
      {pendingEarnings > 0 && (
        <p style={{margin: '4px 0 0', fontSize: 13, opacity: 0.85}}>
          ⏳ £{pendingEarnings.toFixed(2)} pending from active/upcoming sessions
        </p>
      )}
      <button
        onClick={onRequestPayout}
        style={{marginTop: 12, background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.6)', color: 'white', borderRadius: 10, padding: '8px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer'}}
      >
        Request Payout
      </button>
    </div>

    {/* ── Active Guests ── */}
    <h3 style={{fontSize: 18, marginTop: 10, marginBottom: 15}}>Active Guests</h3>
    {activeHostBookings.length === 0 ? (
      // Empty-state tile
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: '#F9F9F9', borderRadius: 16, padding: '16px 18px', marginBottom: 8,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14, background: '#E6F0FF', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Users size={22} color="#0056D2" />
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#1C1C1E' }}>No active guests</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#8E8E93' }}>You'll see live sessions here when drivers check in.</p>
        </div>
      </div>
    ) : (
      activeHostBookings.map(booking => {
        // Work out how long is left on this session, rounded to whole minutes.
        const endsAt = new Date(booking.endTime);
        const minsLeft = Math.max(0, Math.round((endsAt - Date.now()) / 60000));
        // Show "1h 20m" if more than an hour, otherwise just "20m".
        const timeLeft = minsLeft >= 60
          ? `${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m`
          : `${minsLeft}m`;

        return (
          // Green left border + pulsing dot marks this as LIVE
          <div key={booking.id} className="listing-item" style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, borderLeft: '4px solid #34C759'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center'}}>
              <div>
                <div style={{fontWeight: 700, fontSize: 16}}>{booking.address}</div>
                <div style={{color: '#8E8E93', fontSize: 14, marginTop: 4}}>Ends in {timeLeft}</div>
              </div>
              <div className="live-indicator" style={{position: 'static'}}></div>
            </div>
            {/* Action row - message driver + report */}
            <div style={{display: 'flex', gap: 8}}>
              <button
                className="secondary-btn"
                style={{flex: 1, background: '#E6F0FF', marginTop: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 10, padding: '12px'}}
                onClick={() => onMessageDriver(booking)}
              >
                <MessageCircle size={18}/> Message Driver
              </button>
              <button
                className="secondary-btn"
                style={{background: '#FFEBEA', color: '#FF3B30', marginTop: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10, padding: '12px 14px', fontWeight: 600, fontSize: 13}}
                onClick={() => onReport(booking)}
              >
                <Flag size={16}/> Report
              </button>
            </div>
          </div>
        );
      })
    )}

    {/* ── Upcoming Guests ── */}
    <h3 style={{fontSize: 18, marginTop: 25, marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8}}>
      <CalendarClock size={18} color="#0056D2" /> Upcoming Guests
    </h3>
    {upcomingHostBookings.length === 0 ? (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: '#F9F9F9', borderRadius: 16, padding: '16px 18px', marginBottom: 8,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14, background: '#E6F0FF', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CalendarClock size={22} color="#0056D2" />
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#1C1C1E' }}>No upcoming guests</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#8E8E93' }}>Future bookings will appear here before they start.</p>
        </div>
      </div>
    ) : (
      upcomingHostBookings.map(booking => (
        // Blue border for upcoming (not yet live) bookings
        <div key={booking.id} className="listing-item" style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, borderLeft: '4px solid #0056D2'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start'}}>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 700, fontSize: 15}}>{booking.address}</div>
              <div style={{color: '#8E8E93', fontSize: 13, marginTop: 4}}>
                Arriving {fmtDateTime(booking.startTime)}
              </div>
              {/* Little blue chip with duration + earning for this booking */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: '#E6F0FF', borderRadius: 8, padding: '4px 10px',
                marginTop: 8, fontSize: 12, color: '#0056D2', fontWeight: 600,
              }}>
                {booking.duration} hr{booking.duration !== 1 ? 's' : ''} · £{(booking.totalPaid || 0).toFixed(2)} earning
              </div>
            </div>
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <button
              className="secondary-btn"
              style={{flex: 1, background: '#E6F0FF', marginTop: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 10, padding: '12px'}}
              onClick={() => onMessageDriver(booking)}
            >
              <MessageCircle size={18}/> Message Driver
            </button>
          </div>
        </div>
      ))
    )}

    {/* ── Past bookings (last 5 only) ── */}
    <h3 style={{fontSize: 18, marginTop: 25, marginBottom: 15}}>Past Bookings</h3>
    {pastHostBookings.length === 0 ? (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: '#F9F9F9', borderRadius: 16, padding: '16px 18px', marginBottom: 8,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14, background: '#F2F2F7', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CalendarX size={22} color="#8E8E93" />
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#1C1C1E' }}>No bookings yet</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#8E8E93' }}>Completed stays will appear here after guests finish their sessions.</p>
        </div>
      </div>
    ) : (
      pastHostBookings.slice(0, 5).map(booking => (
        <div key={booking.id} className="booking-card" style={{borderLeft: '4px solid #E5E5EA', cursor: 'default'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 700, fontSize: 15}}>{booking.address}</div>
              <div style={{color: '#8E8E93', fontSize: 13, marginTop: 3}}>
                {new Date(booking.timestamp).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}
                {' · '}{booking.duration} hr{booking.duration > 1 ? 's' : ''}
              </div>
              <div style={{color: '#34C759', fontWeight: 600, fontSize: 14, marginTop: 4}}>
                £{(booking.totalPaid || 0).toFixed(2)} earned
              </div>
            </div>
            <button
              className="secondary-btn"
              style={{background: '#FFEBEA', color: '#FF3B30', display: 'flex', alignItems: 'center', gap: 6, borderRadius: 10, padding: '8px 12px', marginTop: 0, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', width: 'auto'}}
              onClick={() => onReport(booking)}
            >
              <Flag size={14}/> Report
            </button>
          </div>
        </div>
      ))
    )}

    {/* ── Reports against this host's listings (only if there are any) ── */}
    {hostReports.length > 0 && (
      <>
        <h3 style={{fontSize: 18, marginTop: 25, marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8}}>
          <AlertTriangle size={18} color="#FF9500" /> Reports ({hostReports.length})
        </h3>
        {hostReports.map(report => (
          <div key={report.id} style={{
            background: '#FFF9EC', border: '1px solid #FFE58F', borderRadius: 14,
            padding: '14px 16px', marginBottom: 10,
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
              <div style={{flex: 1}}>
                <div style={{fontWeight: 700, fontSize: 14, color: '#1C1C1E'}}>{report.category}</div>
                {report.relatedAddress && (
                  <div style={{fontSize: 13, color: '#8E8E93', marginTop: 2}}>{report.relatedAddress}</div>
                )}
                {report.description && (
                  <div style={{fontSize: 13, color: '#3A3A3C', marginTop: 6, lineHeight: 1.4}}>{report.description}</div>
                )}
              </div>
              {/* Orange status pill - always "pending" in this demo */}
              <div style={{
                background: '#FF9500', color: 'white', borderRadius: 8,
                padding: '3px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {report.status || 'pending'}
              </div>
            </div>
            <div style={{fontSize: 12, color: '#8E8E93', marginTop: 8}}>{fmtDate(report.createdAt)}</div>
          </div>
        ))}
      </>
    )}

    {/* ── Your driveways list ── */}
    <h3 style={{fontSize: 18, marginTop: 25, marginBottom: 15}}>Your Driveways</h3>

    {/* Empty-state card with a call-to-action to list first driveway */}
    {hostListings.length === 0 && (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '32px 20px', background: '#fff', borderRadius: 20,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 12,
      }}>
        <div style={{
          width: 60, height: 60, borderRadius: 18, background: '#E6F0FF',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <PlusCircle size={30} color="#0056D2" />
        </div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1C1C1E' }}>No driveways listed</p>
        <p style={{ margin: '8px 0 20px', fontSize: 14, color: '#8E8E93', textAlign: 'center', lineHeight: 1.5 }}>
          List your driveway and start earning money from your empty space.
        </p>
        <button
          className="primary-btn"
          onClick={() => onNavigate('addSpot')}
          style={{ width: 'auto', padding: '12px 28px', fontSize: 15 }}
        >
          List a Driveway
        </button>
      </div>
    )}

    {/* Each driveway listing */}
    {hostListings.map(listing => {
      // Calculate the real avg rating from reviews on this spot (falls
      // back to null if the listing's never been reviewed).
      const listingReviews = (allBookings || []).filter(b => b.spotId === listing.id && b.review);
      const avgRating = listingReviews.length > 0
        ? (listingReviews.reduce((sum, b) => sum + b.review.rating, 0) / listingReviews.length).toFixed(1)
        : null;

      return (
        <div className="listing-item" key={listing.id} style={{flexDirection: 'column', alignItems: 'stretch', gap: 8}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
              <div style={{fontWeight: 700, fontSize: 16}}>{listing.address}</div>
              <div style={{color: '#8E8E93', fontSize: 14, marginTop: 4}}>{listing.details}</div>
              {/* Rating preview - yellow star + numeric + count */}
              {avgRating ? (
                <div style={{display: 'flex', alignItems: 'center', gap: 4, marginTop: 6}}>
                  <Star size={13} fill="#FFCC00" color="#FFCC00" />
                  <span style={{fontSize: 13, fontWeight: 600}}>{avgRating}</span>
                  <span style={{fontSize: 13, color: '#8E8E93'}}>({listingReviews.length} review{listingReviews.length !== 1 ? 's' : ''})</span>
                </div>
              ) : (
                <div style={{fontSize: 13, color: '#C7C7CC', marginTop: 6}}>No reviews yet</div>
              )}
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
              {/* Edit pencil button */}
              <button
                onClick={() => onEditSpot(listing.id)}
                style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0056D2', display: 'flex'}}
              >
                <Pencil size={20} />
              </button>
              {/* Active/inactive toggle switch */}
              <div
                className="toggle-switch"
                style={listing.isActive ? {} : {background: '#E5E5EA'}}
                onClick={() => onToggleListing(listing.id)}
              >
                <div className="toggle-knob" style={listing.isActive ? {} : {right: 'auto', left: 2}}></div>
              </div>
            </div>
          </div>

          {/* Inline preview of the 2 most recent reviews */}
          {listingReviews.length > 0 && (
            <div style={{borderTop: '1px solid #F2F2F7', paddingTop: 10, marginTop: 4}}>
              {listingReviews.slice(0, 2).map((b, i) => (
                <div key={i} style={{marginBottom: 8}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3}}>
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={11} fill={b.review.rating >= s ? '#FFCC00' : 'transparent'} color={b.review.rating >= s ? '#FFCC00' : '#E5E5EA'} />
                    ))}
                    <span style={{fontSize: 11, color: '#8E8E93', marginLeft: 4}}>
                      {new Date(b.review.timestamp).toLocaleDateString('en-GB', {day:'numeric', month:'short'})}
                    </span>
                  </div>
                  {b.review.text && <p style={{margin: 0, fontSize: 13, color: '#3A3A3C'}}>{b.review.text}</p>}
                </div>
              ))}
              {/* "+X more reviews" link if there are more than 2 */}
              {listingReviews.length > 2 && (
                <p style={{margin: 0, fontSize: 13, color: '#0056D2'}}>+{listingReviews.length - 2} more reviews</p>
              )}
            </div>
          )}
        </div>
      );
    })}

    </div>
    {/* Bottom nav bar */}
    <HostNav currentScreen={currentScreen} onNavigate={onNavigate} />
  </div>
);

export default HostDashboardView;
